import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  treatmentPackagesApi,
  treatmentPackagesQueryKeys,
  type PackageInput,
} from "../../shared/api/treatmentPackages";
import { apiError } from "../../shared/api/client";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const money = (n: number) => n.toLocaleString("vi-VN") + " ₫";

/** Cài đặt — Gói liệu trình. Danh mục gói (N buổi, trả trước) để bán cho khách. */
export function TreatmentPackagesSettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: treatmentPackagesQueryKeys.catalog(),
    queryFn: () => treatmentPackagesApi.catalog(false),
  });

  const [form, setForm] = useState<PackageInput>({
    name: "",
    totalSessions: 10,
    priceVnd: 0,
    validityDays: null,
  });
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => treatmentPackagesApi.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: treatmentPackagesQueryKeys.catalog() });
      setMsg("Đã tạo gói.");
      setError(null);
      setForm({ name: "", totalSessions: 10, priceVnd: 0, validityDays: null });
    },
    onError: (e: unknown) => {
      setError(apiError(e).detail);
      setMsg(null);
    },
  });

  const archive = useMutation({
    mutationFn: (id: string) => treatmentPackagesApi.archive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: treatmentPackagesQueryKeys.catalog() }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => treatmentPackagesApi.remove(id),
    onSuccess: () => {
      setBulkError(null);
      qc.invalidateQueries({ queryKey: treatmentPackagesQueryKeys.catalog() });
    },
    onError: (e: unknown) => setBulkError(apiError(e).detail),
  });

  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete(
      [...sel.selectedIds],
      treatmentPackagesApi.remove,
    );
    setBulkPending(false);
    sel.clear();
    qc.invalidateQueries({ queryKey: treatmentPackagesQueryKeys.catalog() });
    setBulkError(
      failed.length ? `${failed.length} gói không xoá được (đang được dùng).` : null,
    );
  }

  const packages = q.data ?? [];

  return (
    <div>
      <PageHeader
        title="Gói liệu trình"
        description="Tạo gói dịch vụ trả trước theo số buổi để bán cho khách (spa, nha, đông y…)."
      />

      <div className="grid max-w-3xl gap-4">
        <Card>
          <CardContent className="p-4">
            <h2 className="mb-2 text-base font-semibold">Danh mục gói</h2>
            {q.isLoading ? (
              <p>Đang tải…</p>
            ) : packages.length === 0 ? (
              <p className="text-muted-foreground">Chưa có gói nào.</p>
            ) : (
              <>
                {bulkError && <p className="text-destructive">{bulkError}</p>}
                <BulkDeleteBar
                  count={sel.count}
                  noun="gói"
                  pending={bulkPending}
                  onClear={sel.clear}
                  onDelete={bulkDelete}
                />
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-9 text-center">
                        <input
                          type="checkbox"
                          aria-label="Chọn tất cả"
                          className="h-4 w-4 rounded border-input accent-primary"
                          checked={
                            packages.length > 0 &&
                            packages.every((p) => sel.selectedIds.has(p.id))
                          }
                          ref={(el) => {
                            if (el)
                              el.indeterminate =
                                packages.some((p) => sel.selectedIds.has(p.id)) &&
                                !packages.every((p) => sel.selectedIds.has(p.id));
                          }}
                          onChange={() => {
                            const next = new Set(sel.selectedIds);
                            if (packages.every((p) => next.has(p.id)))
                              packages.forEach((p) => next.delete(p.id));
                            else packages.forEach((p) => next.add(p.id));
                            sel.setSelectedIds(next);
                          }}
                        />
                      </TableHead>
                      <TableHead>Tên gói</TableHead>
                      <TableHead className="text-right">Số buổi</TableHead>
                      <TableHead className="text-right">Giá</TableHead>
                      <TableHead className="text-right">HSD (ngày)</TableHead>
                      <TableHead>Trạng thái</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packages.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-center">
                          <input
                            type="checkbox"
                            aria-label="Chọn dòng"
                            className="h-4 w-4 rounded border-input accent-primary"
                            checked={sel.selectedIds.has(p.id)}
                            onChange={() => sel.toggle(p.id)}
                          />
                        </TableCell>
                        <TableCell>{p.name}</TableCell>
                        <TableCell className="text-right">{p.totalSessions}</TableCell>
                        <TableCell className="text-right">{money(p.priceVnd)}</TableCell>
                        <TableCell className="text-right">{p.validityDays ?? "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <Badge variant={p.active ? "success" : "muted"}>
                            {p.active ? "Đang bán" : "Ngừng"}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right">
                          <div className="flex justify-end gap-1">
                            {p.active && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => archive.mutate(p.id)}
                              >
                                Ngừng bán
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              disabled={remove.isPending}
                              onClick={() => {
                                if (
                                  confirm(
                                    `Xoá vĩnh viễn gói "${p.name}"? Không thể hoàn tác.`,
                                  )
                                )
                                  remove.mutate(p.id);
                              }}
                            >
                              Xoá
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="grid gap-3 p-4">
            <h2 className="text-base font-semibold">Thêm gói</h2>
            <div className="space-y-1.5">
              <Label htmlFor="tp-name">Tên gói</Label>
              <Input
                id="tp-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="VD: Gói chăm sóc da 10 buổi"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="tp-sessions">Số buổi</Label>
                <Input
                  id="tp-sessions"
                  type="number"
                  min={1}
                  value={form.totalSessions}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, totalSessions: Number.parseInt(e.target.value || "1", 10) }))
                  }
                  className="w-24"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tp-price">Giá (VND)</Label>
                <Input
                  id="tp-price"
                  type="number"
                  min={0}
                  value={form.priceVnd}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, priceVnd: Number.parseInt(e.target.value || "0", 10) }))
                  }
                  className="w-40"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tp-validity">Hạn sử dụng (ngày, tuỳ chọn)</Label>
                <Input
                  id="tp-validity"
                  type="number"
                  min={1}
                  value={form.validityDays ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      validityDays: e.target.value ? Number.parseInt(e.target.value, 10) : null,
                    }))
                  }
                  className="w-40"
                  placeholder="vô thời hạn"
                />
              </div>
            </div>
            {error && <p className="text-destructive">{error}</p>}
            {msg && <p className="text-emerald-600">{msg}</p>}
            <div className="flex justify-end">
              <Button
                type="button"
                disabled={!form.name.trim() || create.isPending}
                onClick={() => {
                  setMsg(null);
                  setError(null);
                  create.mutate();
                }}
              >
                {create.isPending ? "Đang tạo…" : "Thêm gói"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
